import type { ReactNode } from "react";
import { Check, ChevronLeft, Info } from "lucide-react";
import { OrderHeader, useOrderFonts } from "../../order/orderTheme";
import {
  TRIAL_SMS_INBOUND_CODES_NOTE, TRIAL_SMS_OUTBOUND_NOTE, TRIAL_SMS_UNLOCK_NOTE,
} from "@shared/trialSmsCopy";
import { TRIAL_DAYS } from "@shared/onboardingSteps";
import "./trialTheme.css";

/**
 * The frame every trial screen sits in: LimePhone header, a "Step N of M"
 * progress bar, a Back link, then ONE focused panel. The order flow's theme
 * (`.lp-order`) supplies every token; trialTheme.css adds only the pieces the
 * order pages lack.
 */
export function TrialShell({
  step, total, label, onBack, backLabel = "Back", headerRight, wide = false, children,
}: {
  /** 1-based; omit for screens outside the numbered steps (signup, finish). */
  step?: number;
  total?: number;
  /** Short name of the step, read out with the count. */
  label?: string;
  onBack?: (() => void) | null;
  backLabel?: string;
  headerRight?: ReactNode;
  /** Wider column for a step with a side panel (the card step's order summary). */
  wide?: boolean;
  children: ReactNode;
}) {
  useOrderFonts();
  const pct = step && total ? Math.min(100, Math.max(0, (step / total) * 100)) : null;
  return (
    <div className="lp-order lp-trial" data-testid="trial-shell">
      <OrderHeader right={headerRight ?? <span className="lp-secure">{TRIAL_DAYS}-day free trial</span>} />
      <main className={wide ? "lp-trial-main lp-trial-main-wide" : "lp-trial-main"}>
        {(onBack || pct !== null) && (
          <div className="lp-progress-row">
            {onBack ? (
              <button type="button" className="lp-back" onClick={onBack} data-testid="button-trial-back">
                <ChevronLeft className="h-4 w-4" aria-hidden /> {backLabel}
              </button>
            ) : <span />}
            {pct !== null && (
              <span className="lp-progress-label" data-testid="text-trial-progress">
                Step {step} of {total}{label ? ` · ${label}` : ""}
              </span>
            )}
          </div>
        )}
        {pct !== null && (
          <div
            className="lp-progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={step}
            aria-label={`Step ${step} of ${total}`}
          >
            <span style={{ width: `${pct}%` }} />
          </div>
        )}
        {children}
      </main>
    </div>
  );
}

export function StepHead({ title, sub }: { title: ReactNode; sub?: ReactNode }) {
  return (
    <div className="lp-step-head">
      <h1 className="lp-display">{title}</h1>
      {sub && <p>{sub}</p>}
    </div>
  );
}

/** A big tappable card. role=radio inside a radiogroup. */
export function ChoiceCard({
  selected, onClick, icon, children, disabled, testId,
}: {
  selected: boolean;
  onClick: () => void;
  icon?: ReactNode;
  children: ReactNode;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className="lp-choice"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
    >
      {icon && <span className="lp-choice-icon" aria-hidden>{icon}</span>}
      <span className="lp-choice-label">{children}</span>
      <span className="lp-choice-tick" aria-hidden>{selected && <Check className="h-3.5 w-3.5" />}</span>
    </button>
  );
}

export function InfoNote({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <div className="lp-info" role="note" data-testid={testId}>
      <Info className="h-4 w-4" aria-hidden />
      <div>{children}</div>
    </div>
  );
}

/** The two trial texting facts (shared/trialSmsCopy), for US/CA numbers. */
export function TrialSmsNotes({ testId = "note-trial-sms" }: { testId?: string }) {
  return (
    <InfoNote testId={testId}>
      <ul>
        <li>{TRIAL_SMS_OUTBOUND_NOTE}</li>
        <li>{TRIAL_SMS_INBOUND_CODES_NOTE}</li>
        <li className="lp-muted">{TRIAL_SMS_UNLOCK_NOTE}</li>
      </ul>
    </InfoNote>
  );
}
